import { getFirAuth } from '../../Api/firebase'
import type { User } from '../../Api/Users/types'
import { FIREBASE_CONFIG } from '../../Config/Firebase/config'
import { GOOGLE_CLIENT_ID } from '../../Config/Firebase/config'
import { ANALYTICS_TAGS } from '../../Constant'
import { logEvent } from '../analytics/firAnalytics'
import { reloadWindow } from '../windowHelpers/windowHelpers'

export const USER_AUTH_ISSUES = {
  USER_MARKED_AS_LOGGED_IN_BUT_NO_ID: 'User marked as logged in but has no ID',
  USER_MARKED_AS_ANONYMOUS_BUT_ALSO_LOGGED_IN:
    'User marked as anonymous but also logged in',
  USER_MARKED_AS_ANONYMOUS_BUT_HAS_EMAIL: 'Anonymous user should not have email',
}

/**
 * Get user ID from user object, trying multiple possible ID fields
 */
export const getUserId = (user: User | null): string | null => {
  if (!user) return null
  return user.userId || user.uid || null
}

/**
 * Check if Google Sign-In is properly configured
 */
export const checkGoogleSignInConfig = (): {
  isConfigured: boolean
  issues: string[]
} => {
  const issues: string[] = []

  if (!GOOGLE_CLIENT_ID) {
    issues.push('GOOGLE_CLIENT_ID environment variable is not set')
  } else if (GOOGLE_CLIENT_ID.trim() === '') {
    issues.push('GOOGLE_CLIENT_ID environment variable is empty')
  } else if (!GOOGLE_CLIENT_ID.includes('.googleusercontent.com')) {
    issues.push('GOOGLE_CLIENT_ID does not appear to be a valid Google OAuth client ID')
  }

  if (!window?.google?.accounts) {
    issues.push('Google Sign-In script is not loaded')
  }

  const isConfigured = issues.length === 0

  if (!isConfigured) {
    logEvent(ANALYTICS_TAGS.google_config_missing, {
      issues: issues.join(', '),
      hasClientId: !!GOOGLE_CLIENT_ID,
      hasGoogleScript: !!window?.google?.accounts,
      product: 'web',
    })
  }

  return {
    isConfigured,
    issues,
  }
}

/**
 * Get debug information about current auth state
 */
export const getAuthStateDebug = (user: User | null, authLoading: boolean) => {
  return {
    authLoading,
    userExists: !!user,
    isLoggedIn: user?.isLoggedIn || false,
    isAnonymous: user?.isAnonymous || false,
    hasUserId: !!getUserId(user),
    hasEmail: !!user?.email,
    userKeys: user ? Object.keys(user) : [],
  }
}

/**
 * Validate auth state consistency
 */
export const validateAuthState = (
  user: User | null
): { isValid: boolean; issues: string[] } => {
  const issues: string[] = []

  if (!user) {
    return { isValid: true, issues: [] } // No user is valid state
  }

  // Check for inconsistent auth states
  if (user.isLoggedIn && !getUserId(user)) {
    issues.push(USER_AUTH_ISSUES.USER_MARKED_AS_LOGGED_IN_BUT_NO_ID)
  }

  if (user.isAnonymous && user.isLoggedIn) {
    issues.push(USER_AUTH_ISSUES.USER_MARKED_AS_ANONYMOUS_BUT_ALSO_LOGGED_IN)
  }

  if (user.isAnonymous && user.email) {
    issues.push(USER_AUTH_ISSUES.USER_MARKED_AS_ANONYMOUS_BUT_HAS_EMAIL)
  }

  return {
    isValid: issues.length === 0,
    issues,
  }
}

/**
 * Handle Firebase transaction error
 */
export const handleFirebaseTransactionError = (
  errorMessage: string,
  context: string
): boolean => {
  if (
    errorMessage?.includes(
      'Attempt to get records from database without an in-progress transaction'
    )
  ) {
    logEvent(ANALYTICS_TAGS.firebase_transaction_error, {
      error: errorMessage,
      context,
      product: 'web',
    })
    return true
  }
  return false
}

/**
 * Get Firebase auth local storage key
 */
const getFirebaseAuthKey = (): string => {
  return `firebase:authUser:${FIREBASE_CONFIG.apiKey}:[DEFAULT]`
}

export const resetFirebasePersistence = async (): Promise<() => void> => {
  try {
    const auth = await getFirAuth()
    if (auth) {
      await auth.signOut()
      if (typeof window !== 'undefined') {
        const authKey = getFirebaseAuthKey()
        localStorage.removeItem(authKey)
      }
    }
  } catch (error) {
    console.error('Error resetting Firebase persistence:', error)
  }
  return () => {
    if (typeof window !== 'undefined') {
      reloadWindow()
    }
  }
}
